import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import {
    Fab,
    Drawer,
    Box,
    Button,
    Typography,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip,
    Badge,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChecklistIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReplayIcon from '@mui/icons-material/Replay';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import {
    fetchOnboardingProgress,
    openChecklist,
    closeChecklist,
    startTour,
    dismissAllOnboardingSteps,
} from '../../../redux/actions/onboarding';
import {
    filterStepsByAudience,
    detectAudience,
    isAuthRoute,
    resolveTourById,
    resolveTourIdForStep,
    resolveNavigateTo,
    AUDIENCES,
    TOUR_IDS,
} from '../configs/onboarding.config';
import '../onboarding-overlays.css';

// Pequeno atraso pra SPA navigation processar (history.push -> route remount)
// antes do tour iniciar. O tour SEMPRE comeca no step intro (target=body),
// que funciona instantaneamente. Pros steps com target real, ProductTour faz
// aguardamento robusto via MutationObserver com timeout longo + overlay visual
// (sem caixa preta — user ve "Aguardando..." se a tela demora a montar).
const SPA_NAVIGATION_DELAY_MS = 200;

// Paleta — mantida em constantes pra ficar fácil ajustar global
const COLORS = {
    primary: '#8BC34A',
    primaryDark: '#7cb342',
    primaryLight: '#f1f8e9',
    bg: '#ffffff',
    bgSubtle: '#fafafa',
    border: '#e6e8eb',
    text: '#212121',
    textSecondary: '#5f6368',
    textMuted: '#8a8d91',
    completedText: '#7a7a7a',
    completedAccent: '#558b2f',
    iconIdle: '#c4c8cc',
};

class ChecklistFab extends Component {
    state = {
        dismissAllDialogOpen: false,
    };

    openDismissAllDialog = () => this.setState({ dismissAllDialogOpen: true });
    closeDismissAllDialog = () => this.setState({ dismissAllDialogOpen: false });

    handleConfirmDismissAll = () => {
        this.closeDismissAllDialog();
        this.props.dismissAllOnboardingSteps();
    };

    // Refaz o tour System Overview do welcome modal a qualquer momento.
    // Fecha o checklist drawer, navega pra /pt/calleds (se nao estiver) e
    // dispara o tour. Apos o tour terminar, ProductTour reabre o checklist.
    handleReplaySystemOverview = () => {
        const { history, location } = this.props;
        const lang = this.props?.match?.params?.lang || 'pt';
        const targetRoute = `/${lang}/calleds`;
        this.props.closeChecklist();
        if (location?.pathname !== targetRoute && window?.location?.pathname !== targetRoute) {
            history.push(targetRoute);
        }
        // Atraso pra navegacao SPA + drawer fechar antes do tour iniciar
        setTimeout(() => this.props.startTour(TOUR_IDS.SYSTEM_OVERVIEW), 250);
    };

    componentDidMount() {
        const { onboarding, userSession } = this.props;
        // Backend decide se o hirer tem onboarding (audience). Front sempre consulta;
        // se vier audience='' o componente esconde tudo abaixo.
        if (userSession?.user && !onboarding?.loaded && !onboarding?.loading) {
            this.props.fetchOnboardingProgress();
        }
    }

    componentDidUpdate(prevProps) {
        const prevUser = prevProps.userSession?.user;
        const currUser = this.props.userSession?.user;
        if (!prevUser && currUser && !this.props.onboarding?.loaded) {
            this.props.fetchOnboardingProgress();
        }
    }

    handleStepClick = async (step) => {
        const { history, onboarding, userSession } = this.props;
        const demoSeedRefs = onboarding?.demoSeedRefs || {};
        const audienceUser = detectAudience(userSession?.user);
        const tourId = resolveTourIdForStep(step, audienceUser);
        const tour = resolveTourById(tourId, demoSeedRefs);
        const targetPath = resolveNavigateTo(step, demoSeedRefs, audienceUser);
        const currentPath = window?.location?.pathname || '';
        const onTargetPage = targetPath ? currentPath === targetPath : true;

        // Prepare opcional ANTES da navegacao — usado por steps que precisam
        // setar Redux state antes da pagina alvo montar. Hoje so o step
        // CONFIGURE_ORGANIZATION usa (popula state.organizations.organization
        // pra evitar redirect via document.location.href no OrganizationEdit).
        if (typeof step.prepare === 'function') {
            try {
                await step.prepare(demoSeedRefs, this.props.dispatch);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[Onboarding] step.prepare lancou excecao:', err);
            }
        }

        // Fecha o drawer ja (UX imediato — usuario nao quer ver checklist + tour ao mesmo tempo)
        this.props.closeChecklist();

        // Sem tour: so navega
        if (!tour) {
            if (targetPath && !onTargetPage) history.push(targetPath);
            return;
        }

        // Navega pra rota alvo (se precisar) e dispara o tour direto.
        // Tour comeca no step intro (target=body) que funciona em qualquer pagina.
        // Pros steps com target real, ProductTour aguarda DOM hidratar via
        // MutationObserver com timeout longo + overlay "Aguardando carregamento..."
        if (targetPath && !onTargetPage) {
            history.push(targetPath);
        }
        setTimeout(() => this.props.startTour(tourId), SPA_NAVIGATION_DELAY_MS);
    };

    isStepCompleted = (stepId) => {
        const step = this.props.onboarding?.steps?.find((s) => s.stepId === stepId);
        return !!step?.completed;
    };

    renderStepItem = (step) => {
        const { intl } = this.props;
        const completed = this.isStepCompleted(step.id);
        const hasTour = !!step.tourId;

        const itemContent = (
            <ListItemButton
                onClick={() => this.handleStepClick(step)}
                sx={{
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    borderLeft: completed
                        ? `4px solid ${COLORS.primary}`
                        : `4px solid ${COLORS.border}`,
                    backgroundColor: COLORS.bg,
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                    '&:hover': {
                        backgroundColor: COLORS.bgSubtle,
                        borderColor: completed ? COLORS.primary : '#cfd3d8',
                        borderLeftColor: COLORS.primary,
                    },
                    py: 1.25,
                    px: 1.5,
                    alignItems: 'flex-start',
                }}
            >
                <ListItemIcon sx={{ minWidth: 36, mt: '2px' }}>
                    {completed ? (
                        <CheckCircleIcon sx={{ color: COLORS.primary, fontSize: 22 }} />
                    ) : (
                        <RadioButtonUncheckedIcon sx={{ color: COLORS.iconIdle, fontSize: 22 }} />
                    )}
                </ListItemIcon>

                <ListItemText
                    disableTypography
                    primary={
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: completed ? COLORS.completedText : COLORS.text,
                                textDecoration: completed ? 'line-through' : 'none',
                                lineHeight: 1.35,
                            }}
                        >
                            {intl.formatMessage({ id: step.labelKey })}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: COLORS.textSecondary,
                                mt: 0.5,
                                lineHeight: 1.4,
                            }}
                        >
                            {intl.formatMessage({ id: step.descriptionKey })}
                        </Typography>
                    }
                />

                {/* Affordance lateral: replay quando completed+tour, chevron caso contrario */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mt: '2px' }}>
                    {completed && hasTour ? (
                        <ReplayIcon sx={{ color: COLORS.iconIdle, fontSize: 18 }} />
                    ) : !completed ? (
                        <ChevronRightIcon sx={{ color: COLORS.iconIdle, fontSize: 20 }} />
                    ) : null}
                </Box>
            </ListItemButton>
        );

        return (
            <ListItem key={step.id} disablePadding sx={{ mb: 1 }}>
                {completed && hasTour ? (
                    <Tooltip
                        title={intl.formatMessage({ id: 'onboarding.checklist.replay.tooltip' })}
                        placement="left"
                        arrow
                    >
                        <Box sx={{ width: '100%' }}>{itemContent}</Box>
                    </Tooltip>
                ) : (
                    itemContent
                )}
            </ListItem>
        );
    };

    render() {
        const { onboarding, userSession, intl, location } = this.props;
        if (!userSession?.user) return null;

        // Pre-login
        if (isAuthRoute(location?.pathname)) return null;
        if (!localStorage.getItem('accesstoken')) return null;

        // Backend ainda nao respondeu ou audience='' (onboarding desligado pra esse hirer)
        if (!onboarding?.loaded) return null;
        if (!onboarding?.audience) return null;
        if (!onboarding?.steps?.length) return null;

        // Perfis que fazem sentido pro onboarding: admin, agent e requester (nao cliente puro sem role mapeada)
        const audienceUser = detectAudience(userSession.user);
        if (
            audienceUser !== AUDIENCES.ADMIN &&
            audienceUser !== AUDIENCES.AGENT &&
            audienceUser !== AUDIENCES.REQUESTER
        ) return null;

        // Filtro requiresDemoRef: steps que dependem de dados (chamado/org/time)
        // sao ocultados quando o backend nao retornou id correspondente em
        // demoSeedRefs (ambiente dedicated recem-criado sem dados nenhum).
        // Trial e dedicated com qualquer dado continuam mostrando todos os steps —
        // backend tem fallback (is_demo first, qualquer registro depois).
        const demoSeedRefs = onboarding?.demoSeedRefs || {};
        const steps = filterStepsByAudience(audienceUser)
            .filter((s) => !s.requiresDemoRef || demoSeedRefs[s.requiresDemoRef] != null)
            .sort((a, b) => a.order - b.order);
        const completedCount = steps.filter((s) => this.isStepCompleted(s.id)).length;
        const totalCount = steps.length;
        const progressPercent =
            totalCount === 0 ? 0 : Math.round((completedCount * 100) / totalCount);

        const isOpen = !!onboarding?.isChecklistOpen;
        const remaining = totalCount - completedCount;

        // Quando user clica "Ja vi todos os passos", allCompleted=true e
        // ocultamos o FAB. MAS o Drawer continua acessivel via menu Acoes
        // Rapidas ("Tour de boas-vindas") — entao se isChecklistOpen=true,
        // renderizamos o Drawer mesmo com allCompleted. Caso contrario
        // (allCompleted + drawer fechado): nada renderiza.
        const hideFab = !!onboarding?.allCompleted;
        if (hideFab && !isOpen) return null;

        return (
            <>
                {!hideFab && (<Tooltip
                    title={intl.formatMessage({ id: 'onboarding.checklist.tooltip' })}
                    placement="left"
                >
                    <Fab
                        onClick={() => this.props.openChecklist()}
                        sx={{
                            position: 'fixed',
                            bottom: 90,
                            right: 24,
                            backgroundColor: COLORS.primary,
                            color: '#fff',
                            boxShadow: '0 4px 14px rgba(139, 195, 74, 0.35)',
                            '&:hover': { backgroundColor: COLORS.primaryDark },
                            zIndex: 1200,
                        }}
                    >
                        <Badge
                            badgeContent={remaining}
                            color="error"
                            invisible={remaining === 0}
                        >
                            <ChecklistIcon />
                        </Badge>
                    </Fab>
                </Tooltip>)}

                <Drawer
                    anchor="right"
                    open={isOpen}
                    onClose={() => this.props.closeChecklist()}
                    className="mexx-onboarding-checklist-drawer"
                    PaperProps={{
                        sx: {
                            width: { xs: '100%', sm: 440 },
                            backgroundColor: COLORS.bg,
                            backgroundImage: 'none',
                        },
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: COLORS.bg,
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.5,
                                }}
                            >
                                <Typography
                                    sx={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}
                                >
                                    {intl.formatMessage({ id: 'onboarding.checklist.title' })}
                                </Typography>
                                <IconButton
                                    onClick={() => this.props.closeChecklist()}
                                    size="small"
                                    sx={{ color: COLORS.textMuted }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Typography
                                sx={{ fontSize: 13, color: COLORS.textSecondary, mb: 1.5 }}
                            >
                                {intl.formatMessage({ id: 'onboarding.checklist.subtitle' })}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                                <Button
                                    onClick={this.handleReplaySystemOverview}
                                    size="small"
                                    variant="contained"
                                    startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        backgroundColor: COLORS.completedAccent,
                                        color: '#ffffff',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 1.25, py: 0.4,
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 6px rgba(85, 139, 47, 0.35)',
                                        '&:hover': {
                                            backgroundColor: '#3e6924',
                                            boxShadow: '0 3px 10px rgba(62, 105, 36, 0.45)',
                                        },
                                    }}
                                >
                                    {intl.formatMessage({ id: 'onboarding.checklist.replayOverview.button' })}
                                </Button>
                                <Button
                                    onClick={this.openDismissAllDialog}
                                    size="small"
                                    variant="contained"
                                    startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        backgroundColor: COLORS.primary,
                                        color: '#ffffff',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 1.25, py: 0.4,
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 6px rgba(139, 195, 74, 0.35)',
                                        '&:hover': {
                                            backgroundColor: COLORS.primaryDark,
                                            boxShadow: '0 3px 10px rgba(124, 179, 66, 0.45)',
                                        },
                                    }}
                                >
                                    {intl.formatMessage({ id: 'onboarding.checklist.dismissAll.button' })}
                                </Button>
                            </Box>

                            {/* Progress */}
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 0.75,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: COLORS.textSecondary,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {intl.formatMessage(
                                            { id: 'onboarding.checklist.progress' },
                                            { completed: completedCount, total: totalCount }
                                        )}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: COLORS.completedAccent,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {progressPercent}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPercent}
                                    sx={{
                                        height: 6,
                                        borderRadius: 999,
                                        backgroundColor: '#eef0f2',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: COLORS.primary,
                                            borderRadius: 999,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: COLORS.border }} />

                        {/* Steps list */}
                        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
                            <List disablePadding>{steps.map(this.renderStepItem)}</List>
                        </Box>
                    </Box>
                </Drawer>

                {/* Modal de confirmacao do "Ja vi todos os passos" */}
                <Dialog
                    open={this.state.dismissAllDialogOpen}
                    onClose={this.closeDismissAllDialog}
                    PaperProps={{ sx: { borderRadius: '12px', maxWidth: 460 } }}
                >
                    <DialogTitle sx={{ fontSize: 17, fontWeight: 700, color: COLORS.text, pb: 1 }}>
                        {intl.formatMessage({ id: 'onboarding.checklist.dismissAll.dialog.title' })}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.55 }}>
                            {intl.formatMessage({ id: 'onboarding.checklist.dismissAll.dialog.content' })}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            onClick={this.closeDismissAllDialog}
                            sx={{
                                color: COLORS.textSecondary, textTransform: 'none',
                                fontSize: 13, fontWeight: 500,
                            }}
                        >
                            {intl.formatMessage({ id: 'onboarding.checklist.dismissAll.dialog.cancel' })}
                        </Button>
                        <Button
                            onClick={this.handleConfirmDismissAll}
                            variant="contained"
                            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                backgroundColor: COLORS.primary, color: '#fff',
                                textTransform: 'none', fontSize: 13, fontWeight: 600,
                                borderRadius: '8px', boxShadow: '0 2px 6px rgba(139, 195, 74, 0.35)',
                                '&:hover': { backgroundColor: COLORS.primaryDark },
                            }}
                        >
                            {intl.formatMessage({ id: 'onboarding.checklist.dismissAll.dialog.confirm' })}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    userSession: state.userSession,
    onboarding: state.onboarding,
});

const mapDispatchToProps = (dispatch) => ({
    ...bindActionCreators(
        { fetchOnboardingProgress, openChecklist, closeChecklist, startTour, dismissAllOnboardingSteps },
        dispatch
    ),
    // Expoe dispatch puro pra step.prepare (usado pelo CONFIGURE_ORGANIZATION
    // que precisa dispatcher addCurrent na action de organizations antes
    // de navegar pra /pt/organization-edit).
    dispatch,
});

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChecklistFab)));
